import { useGlobalSearchContext } from '../../../../../../context/GlobalSearchContext';
import {
  getHasHiddenPrice,
  getMinimumProductQuantity,
  getProductCategory,
} from '@monofront/business/utils';
import { trackEvent } from '@monofront/tracking';
import { AddToShoppingCartSource, HitProps, IProduct } from '@monofront/types';
import { ALGOLIA_INSIGHTS_EVENT_NAME } from '@monofront/business/constants';

import {
  HorizontalProductCard,
  HorizontalProductComeBackSoon,
  HorizontalProductImage,
  HorizontalProductPrice,
  ProductBundle,
  ProductFormat,
  ProductFormatAndUnitPrice,
  ProductOutOfStock,
  ProductPfand,
  ProductPromotionalOffer,
  ProductVendor,
} from '@monofront/design-system/components/ProductCard';
import useProductToShoppingCart from '@hooks/cart/useProductToShoppingCart';
import { useTranslations } from 'next-intl';
import { useCardProductInfos } from '@hooks/businessContainer';
import { useAuthentication } from '@context/businessContainer';
import { useNotifyMe } from '@monofront/business/hooks';
import { toast } from 'react-toastify';
import { HorizontalHiddenPrice } from '@monofront/design-system/components/ProductCard/components/ProductPrice';
import { HorizontalSearchProductAddToCart } from '@monofront/design-system/components/ProductCard/components/ProductAddToCart';
import { HorizontalSearchProductTitle } from '@monofront/design-system/components/ProductCard/components/ProductTitle';
import { HorizontalSearchProductBadges } from '@monofront/design-system/components/ProductCard/components/ProductBadges';

type ListProductsProps = HitProps<IProduct>;

const ListProducts: React.FC<ListProductsProps> = ({ hit, sendEvent }) => {
  const { setIsGlobalSearchResultsPanelOpen } = useGlobalSearchContext();

  const onCardProductClick = () => {
    sendEvent('click', hit, ALGOLIA_INSIGHTS_EVENT_NAME.CLICK_PRODUCT);

    setIsGlobalSearchResultsPanelOpen(false);

    trackEvent({
      type: 'LFSearchRecommendationClick',
      data: {
        recommendationType: 'product',
        itemId: hit.sku,
        itemName: hit.title,
        itemBrand: hit.vendor,
        itemCategory: getProductCategory(hit) ?? 'NO_CATEGORY',
        isSearchEmpty: false,
      },
    });
  };

  const {
    localProductQuantity,
    currentProductQuantity,
    incrementCount,
    decrementCount,
  } = useProductToShoppingCart(hit, {
    source: AddToShoppingCartSource.SEARCH_INPUT,
  });

  const tCatalog = useTranslations('Catalog');
  const { isAntiWaste } = useCardProductInfos(hit);
  const { authUser, isAuthenticated } = useAuthentication();
  const shouldHidePrice = Boolean(!isAuthenticated && getHasHiddenPrice(hit));

  const { loading, notifyMe } = useNotifyMe();
  const hasPfand = hit.meta.finance?.pfand;

  const hasEnoughStock =
    hit.inventory_quantity >= getMinimumProductQuantity(hit.tags);

  hit.inventory_available = hasEnoughStock;

  const handleNotifyMe = async (
    email,
    handleSuccess,
    handleError,
  ): Promise<void> => {
    await notifyMe({
      email,
      sku: hit.sku,
      successCallback: (messageKey) => {
        toast(tCatalog(messageKey));
        handleSuccess?.();
      },
      errorCallback: (errorMessageKey) => {
        toast(tCatalog(errorMessageKey), {
          progressClassName: 'toast-error',
        });
        handleError?.(errorMessageKey);
      },
    });
  };

  return (
    <>
      <div
        onClick={onCardProductClick}
        key={hit.objectID}
        className="suggestion tw-h-full"
      >
        <HorizontalProductCard
          product={hit}
          isInCart={currentProductQuantity > 0}
          key={hit.objectID}
        >
          <HorizontalProductImage />
          <HorizontalSearchProductBadges />
          <HorizontalSearchProductTitle />
          <ProductVendor />
          <ProductFormatAndUnitPrice quantity={localProductQuantity ?? 0} />
          <ProductBundle />
          <ProductPromotionalOffer quantity={localProductQuantity ?? 0} />
          <ProductFormat />
          {!shouldHidePrice && <HorizontalProductPrice />}
          {shouldHidePrice && <HorizontalHiddenPrice />}
          {hasPfand && <ProductPfand />}
          <HorizontalSearchProductAddToCart
            handleAddToCart={incrementCount}
            handleRemoveFromCart={decrementCount}
            quantity={localProductQuantity ?? 0}
          />
          {isAntiWaste ? (
            <ProductOutOfStock />
          ) : (
            <HorizontalProductComeBackSoon
              userEmail={authUser?.email}
              handleNotifyMe={handleNotifyMe}
              isNotifyMeLoading={loading}
            />
          )}
        </HorizontalProductCard>
      </div>
      <style jsx>{`
        .suggestion {
          transition: 0.3s;
          border-radius: var(--rd-2);
        }

        .suggestion:hover {
          background-color: var(--clr-primary-5);
        }
      `}</style>
    </>
  );
};

export default ListProducts;
